import { useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => (n != null ? n : "");
const fmtCurrency = (n) => (n != null ? n : 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "");
const fmtList = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");

/** Build a human-readable label from filters for the sheet name / filename */
const buildLabel = (filters) => {
  if (filters.fromDate && filters.toDate)
    return `${filters.fromDate}_to_${filters.toDate}`;
  if (filters.month && filters.year)
    return `${new Date(0, filters.month - 1).toLocaleString("default", { month: "short" })}_${filters.year}`;
  if (filters.year) return `Year_${filters.year}`;
  if (filters.month)
    return new Date(0, filters.month - 1).toLocaleString("default", { month: "long" });
  return "All";
};

// ─── column definitions ────────────────────────────────────────────────────────
const COLUMNS = [
  { header: "#",               key: (b, i) => i + 1 },
  { header: "Invoice No",      key: (b)    => b.invoiceNo || "" },
  { header: "Sale Date",       key: (b)    => fmtDate(b.saleDate) },
  { header: "Location",        key: (b)    => b.location || "" },
  { header: "Model",           key: (b)    => b.model || "" },
  { header: "SKU Code",        key: (b)    => b.skuCode || "" },
  { header: "Color",           key: (b)    => b.color || "" },
  { header: "Engine Number",   key: (b)    => b.engineNumber || "" },
  { header: "Chasis Number",   key: (b)    => b.chasisNumber || "" },
  { header: "Key Number",      key: (b)    => b.keyNumber || "" },
  { header: "Customer Name",   key: (b)    => b.customerName || "" },
  { header: "Father / C/O",    key: (b)    => b.fatherName || "" },
  { header: "Mobile",          key: (b)    => fmtList(b.mobile) },
  { header: "Address",         key: (b)    => b.address || "" },
  { header: "Village",         key: (b)    => b.village || "" },
  { header: "Post Office",     key: (b)    => b.postOffice || "" },
  { header: "Police Station",  key: (b)    => b.policeStation || "" },
  { header: "PIN Code",        key: (b)    => b.pinCode || "" },
  { header: "Landmark",        key: (b)    => b.landmark || "" },
  { header: "GSTIN",           key: (b)    => b.GSTIN || "" },
  { header: "Reg. No",           key: (b)    => b.regNo || "" },
   
  { header: "ExShowroom Price (₹)", key: (b) => fmtCurrency(b.salePrice) },
  { header: "ORP (₹)",         key: (b)    => fmtCurrency(b.orp) },
  { header: "Discount (₹)",    key: (b)    => fmtCurrency(b.discount) },
  { header: "Net Amount (₹)",  key: (b)    => fmtCurrency((b.salePrice || 0) - (b.discount || 0)) },
  { header: "Cash Amount (₹)", key: (b)    => fmtCurrency(b.cashAmount) },
  { header: "Sale Type",       key: (b)    => b.saleType || "" },
  // Finance
  { header: "Finance Company", key: (b)    => b.finance?.companyName || "" },
  { header: "Finance Amount (₹)", key: (b) => fmtCurrency(b.finance?.amount) },
  // Exchange
  { header: "Exchange Owner",     key: (b) => b.exchange?.ownerName || "" },
  { header: "Exchange Phone",     key: (b) => b.exchange?.phone || "" },
  { header: "Exchange Model",     key: (b) => b.exchange?.model || "" },
  { header: "Exchange Reg No",    key: (b) => b.exchange?.registrationNumber || "" },
  { header: "Exchange Price (₹)", key: (b) => fmtCurrency(b.exchange?.exchangePrice) },
  // Due
  { header: "Due Status",          key: (b) => b.due?.status || "PAID" },
  { header: "Total Due (₹)",       key: (b) => fmtCurrency(b.due?.totalAmount) },
  { header: "Paid So Far (₹)",     key: (b) => fmtCurrency(b.due?.paidAmount) },
  { header: "Remaining Due (₹)",   key: (b) => fmtCurrency(b.due?.remainingAmount) },
    { header: "Due Note", key: (b) => b.due?.note || "" },
  // Charges
  { header: "RTO (₹)",        key: (b) => fmtCurrency(b.charges?.rto) },
  { header: "Insurance (₹)",  key: (b) => fmtCurrency(b.charges?.insurance) },
  // Expenses
  { header: "Helmet",         key: (b) => fmt(b.expenses?.helmet) },
  { header: "Seat Cover",     key: (b) => fmt(b.expenses?.seatCover) },
  { header: "Bike Cover",     key: (b) => fmt(b.expenses?.bikeCover) },
  { header: "Transport",      key: (b) => fmt(b.expenses?.transport) },
  { header: "Petrol",      key: (b) => fmt(b.expenses?.petrol) },
  { header: "Legguard",      key: (b) => fmt(b.expenses?.legguard) },
  
  { header: "Broker",         key: (b) => fmt(b.expenses?.broker) },
  { header: "TCR",            key: (b) => fmt(b.expenses?.tcr) },
  { header: "Reg Broker",     key: (b) => fmt(b.expenses?.regBroker) },
  { header: "AMC / RSA",      key: (b) => fmt(b.expenses?.amcRsa) },
  // Gifts
  { header: "Gifts",          key: (b) => (b.gifts || []).map((g) => `${g.name} x${g.quantity}`).join("; ") },
  // Salesman
  { header: "Salesman Name",      key: (b) => b.salesman?.name || "" },
  { header: "Salesman Incentive (₹)", key: (b) => fmtCurrency(b.salesman?.incentive) },
  // Sold by
  { header: "Sold By",       key: (b) => b.soldBy?.name || "" },
  { header: "Sold By Role",  key: (b) => b.soldBy?.role || "" },
  { header: "Sold By Email", key: (b) => b.soldBy?.email || "" },
  { header: "Remarks",       key: (b) => b.remarks || "" },
];

// ─── XLSX cell styles (applied via openpyxl-style keys — SheetJS pro only)
// We use plain SheetJS (free) so we apply styles via XML-based cell format hints:
const HEADER_STYLE = {
  font:      { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 9 },
  fill:      { fgColor: { rgb: "1565C0" }, patternType: "solid" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top:    { style: "thin", color: { rgb: "AAAAAA" } },
    bottom: { style: "thin", color: { rgb: "AAAAAA" } },
    left:   { style: "thin", color: { rgb: "AAAAAA" } },
    right:  { style: "thin", color: { rgb: "AAAAAA" } },
  },
};

const cellStyle = (isDue) => ({
  font:      { name: "Arial", sz: 9 },
  fill:      { fgColor: { rgb: isDue ? "FFF8F5" : "FFFFFF" }, patternType: "solid" },
  alignment: { vertical: "center" },
  border: {
    top:    { style: "hair", color: { rgb: "E0E0E0" } },
    bottom: { style: "hair", color: { rgb: "E0E0E0" } },
    left:   { style: "hair", color: { rgb: "E0E0E0" } },
    right:  { style: "hair", color: { rgb: "E0E0E0" } },
  },
});

const numberStyle = (isDue) => ({
  ...cellStyle(isDue),
  numFmt: '₹#,##0;(₹#,##0);"-"',
  alignment: { horizontal: "right", vertical: "center" },
});

// ─── MAIN EXPORT FUNCTION ─────────────────────────────────────────────────────
const generateExcel = (data, filters) => {
  const wb = XLSX.utils.book_new();
  const label = buildLabel(filters);

  // ── Sheet 1: All Bikes (flat) ────────────────────────────────────────────
  const allRows = data.map((b, i) => COLUMNS.map((c) => c.key(b, i)));
  const allWs   = XLSX.utils.aoa_to_sheet([COLUMNS.map((c) => c.header), ...allRows]);

  // Apply header styles
  COLUMNS.forEach((_, ci) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (!allWs[cellRef]) allWs[cellRef] = { t: "s", v: COLUMNS[ci].header };
    allWs[cellRef].s = HEADER_STYLE;
  });

  // Apply row styles + column widths
  data.forEach((b, ri) => {
    const isDue = b?.due?.status === "DUE";
    COLUMNS.forEach((col, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
      if (allWs[cellRef]) {
        const isNum = typeof allRows[ri][ci] === "number" && ci >= 20;
        allWs[cellRef].s = isNum ? numberStyle(isDue) : cellStyle(isDue);
      }
    });
  });

  // Column widths
  allWs["!cols"] = COLUMNS.map((c) => ({
    wch: Math.max(c.header.length + 2, 14),
  }));
  allWs["!rows"] = [{ hpt: 28 }]; // header row height

  // Freeze top row
  allWs["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft" };

  XLSX.utils.book_append_sheet(wb, allWs, "All Bikes");

  // ── Sheet 2: Location Summary ────────────────────────────────────────────
  const locations = [...new Set(data.map((b) => b.location || "Other"))];
  const summaryHeaders = [
    "Location", "Total Bikes", "CASH", "FINANCE", "EX+FIN", "CASH+EX",
    "PAID", "DUE", "Total Sale Price (₹)", "Total ORP (₹)",
    "Total Finance Amt (₹)", "Total Due Remaining (₹)"
  ];

  const summaryRows = locations.map((loc) => {
    const bikes = data.filter((b) => (b.location || "Other") === loc);
    return [
      loc,
      bikes.length,
      bikes.filter((b) => b.saleType === "CASH").length,
      bikes.filter((b) => b.saleType === "FINANCE").length,
      bikes.filter((b) => b.saleType === "EX+FIN").length,
      bikes.filter((b) => b.saleType === "CASH+EX").length,
      bikes.filter((b) => b.due?.status !== "DUE").length,
      bikes.filter((b) => b.due?.status === "DUE").length,
      bikes.reduce((s, b) => s + (b.salePrice || 0), 0),
      bikes.reduce((s, b) => s + (b.orp || 0), 0),
      bikes.reduce((s, b) => s + (b.finance?.amount || 0), 0),
      bikes.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0),
    ];
  });

  // Totals row
  if (summaryRows.length > 0) {
    summaryRows.push([
      "TOTAL",
      data.length,
      data.filter((b) => b.saleType === "CASH").length,
      data.filter((b) => b.saleType === "FINANCE").length,
      data.filter((b) => b.saleType === "EX+FIN").length,
      data.filter((b) => b.saleType === "CASH+EX").length,
      data.filter((b) => b.due?.status !== "DUE").length,
      data.filter((b) => b.due?.status === "DUE").length,
      data.reduce((s, b) => s + (b.salePrice || 0), 0),
      data.reduce((s, b) => s + (b.orp || 0), 0),
      data.reduce((s, b) => s + (b.finance?.amount || 0), 0),
      data.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0),
    ]);
  }

  const sumWs = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);

  summaryHeaders.forEach((h, ci) => {
    const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
    if (!sumWs[ref]) sumWs[ref] = { t: "s", v: h };
    sumWs[ref].s = HEADER_STYLE;
  });

  // Bold totals row
  const totalRowIndex = summaryRows.length; // 1-based after header
  summaryHeaders.forEach((_, ci) => {
    const ref = XLSX.utils.encode_cell({ r: totalRowIndex, c: ci });
    if (sumWs[ref]) {
      sumWs[ref].s = {
        font: { bold: true, name: "Arial", sz: 9 },
        fill: { fgColor: { rgb: "E3F2FD" }, patternType: "solid" },
        alignment: { horizontal: ci > 0 ? "right" : "left", vertical: "center" },
        border: { top: { style: "medium", color: { rgb: "1565C0" } } },
      };
    }
  });

  sumWs["!cols"] = summaryHeaders.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, sumWs, "Location Summary");

  // ── Sheet 3: DUE List ────────────────────────────────────────────────────
  const dueData = data.filter((b) => b?.due?.status === "DUE");
  if (dueData.length > 0) {
    const dueHeaders = [
      "#", "Invoice", "Sale Date", "Location", "Model", "Customer", "Mobile",
      "ExShowroom (₹)", "Sale Type", "Total Due (₹)", "Paid (₹)", "Remaining (₹)", "Due Note", "Address"
    ];
    const dueRows = dueData.map((b, i) => [
      i + 1,
      b.invoiceNo || "",
      fmtDate(b.saleDate),
      b.location || "",
      b.model || "",
      b.customerName || "",
      fmtList(b.mobile),
      fmtCurrency(b.salePrice),
      b.saleType || "",
      fmtCurrency(b.due?.totalAmount),
      fmtCurrency(b.due?.paidAmount),
      fmtCurrency(b.due?.remainingAmount),
      b.due?.note || "",
      b.address || "",
    ]);

    const dueWs = XLSX.utils.aoa_to_sheet([dueHeaders, ...dueRows]);
    dueHeaders.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!dueWs[ref]) dueWs[ref] = { t: "s", v: dueHeaders[ci] };
      dueWs[ref].s = { ...HEADER_STYLE, fill: { fgColor: { rgb: "E65100" }, patternType: "solid" } };
    });
    dueRows.forEach((_, ri) => {
      dueHeaders.forEach((_, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (dueWs[ref]) dueWs[ref].s = cellStyle(true);
      });
    });
    dueWs["!cols"] = dueHeaders.map(() => ({ wch: 20 }));
    XLSX.utils.book_append_sheet(wb, dueWs, "Due Payments");
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const filename = `SoldBikes_${label}_${new Date().toISOString().split("T")[0]}.xlsx`;
  XLSX.writeFile(wb, filename, { bookType: "xlsx", type: "binary", cellStyles: true });
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const ExportexcelSell = ({ data = [], filters = {} }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = () => {
    if (!data.length) return;
    setLoading(true);
    setTimeout(() => {
      try {
        generateExcel(data, filters);
      } finally {
        setLoading(false);
      }
    }, 100);
  };

  return (
    <Tooltip title={`Export ${data.length} records to Excel`} arrow>
      <span>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <FileDownloadIcon />}
          onClick={handleExport}
          disabled={loading || !data.length}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: "0.75rem",
            color: "#2e7d32",
            borderColor: "#2e7d32",
            "&:hover": { bgcolor: "#e8f5e9", borderColor: "#1b5e20" },
          }}
        >
          {loading ? "Generating…" : "Export Excel"}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ExportexcelSell;